// Cliente tipado dos endpoints REST do Agno AgentOS.
// Usa endpoint+token "ativo" por padrão, mas aceita override pra fetchar
// catalog de outros endpoints.

import { activeUrl, activeToken, activeDbId } from '../store/global';
import type {
  TeamSummary,
  AgentSummary,
  SessionSummary,
  SessionRunSummary,
  MemorySummary,
  KnowledgeContent,
  Schedule,
  Approval,
  EvalRun,
  TraceSummary,
} from '../types/agno-api';

export class ApiError extends Error {
  constructor(public status: number, public body: string, message?: string) {
    super(message || `HTTP ${status}: ${body.slice(0, 200)}`);
    this.name = 'ApiError';
  }
}

/**
 * Mensagem de erro pra tela. Um 400 que cita `db_id` quase sempre é catálogo
 * velho no localStorage (alvo sem `db_id` num AgentOS com vários DBs) — aí
 * acrescenta a dica de recarregar o endpoint.
 */
export function describeApiError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (e instanceof ApiError && e.status === 400 && e.body.includes('db_id')) {
    return `${msg} — Atualize o endpoint na sidebar (catálogo desatualizado)`;
  }
  return msg;
}

export interface RequestOverride {
  baseUrl?: string;
  token?: string | null;
  auth?: boolean;
}

async function request<T>(
  path: string,
  init: RequestInit & RequestOverride = {},
): Promise<T> {
  const { auth = true, baseUrl, token, headers, ...rest } = init;
  const base = (baseUrl ?? activeUrl.value).replace(/\/$/, '');
  const url = `${base}${path}`;
  const h = new Headers(headers);
  if (auth) {
    const tok = token ?? activeToken.value;
    if (tok) h.set('Authorization', `Bearer ${tok}`);
  }
  if (!(rest.body instanceof FormData) && !h.has('Content-Type') && rest.body) {
    h.set('Content-Type', 'application/json');
  }
  const res = await fetch(url, { ...rest, headers: h });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body);
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const health = (o?: RequestOverride) => request<{ status: string }>('/health', { auth: false, ...o });
export const info = (o?: RequestOverride) => request<Record<string, unknown>>('/info', o);
export const config = (o?: RequestOverride) => request<Record<string, unknown>>('/config', o);

export const listTeams = (o?: RequestOverride) => request<TeamSummary[]>('/teams', o);
export const getTeam = (id: string, o?: RequestOverride) => request<TeamSummary>(`/teams/${id}`, o);
export const getTeamRuns = (id: string) => request<SessionRunSummary[]>(`/teams/${id}/runs`);
export const getTeamRun = (id: string, runId: string) =>
  request<SessionRunSummary>(`/teams/${id}/runs/${runId}`);
export const cancelTeamRun = (id: string, runId: string) =>
  request(`/teams/${id}/runs/${runId}/cancel`, { method: 'POST' });

export const listAgents = (o?: RequestOverride) => request<AgentSummary[]>('/agents', o);
export const getAgent = (id: string) => request<AgentSummary>(`/agents/${id}`);
export const getAgentRuns = (id: string) => request<SessionRunSummary[]>(`/agents/${id}/runs`);
export const getAgentRun = (id: string, runId: string) =>
  request<SessionRunSummary>(`/agents/${id}/runs/${runId}`);
export const cancelAgentRun = (id: string, runId: string) =>
  request(`/agents/${id}/runs/${runId}/cancel`, { method: 'POST' });

// ─── Sessões e memórias ──────────────────────────────────────────────────────
//
// Todas as rotas /sessions* e /memories* do AgentOS (Agno 3.1.1) aceitam
// `db_id` na query. Com mais de um DB registrado, omitir o param devolve 400
// ("The db_id query parameter is required when using multiple databases").
// Mandamos o `db_id` do alvo ativo (`activeDbId`); quando o alvo não informa
// (AgentOS antigo ou DB único) o param fica de fora e nada muda.

export type SessionType = 'agent' | 'team' | 'workflow';

type QueryValue = string | number | undefined | null;

/** Monta `?a=1&b=2` ignorando undefined/null/''. `db_id` do alvo ativo entra sozinho. */
function scopedQuery(params: Record<string, QueryValue> = {}): string {
  const merged: Record<string, QueryValue> = { db_id: activeDbId.value, ...params };
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(merged)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const out = qs.toString();
  return out ? `?${out}` : '';
}

const seg = (v: string) => encodeURIComponent(v);

export const listSessions = (params?: {
  type?: SessionType;
  component_id?: string;
  user_id?: string;
  limit?: number;
}) =>
  request<SessionSummary[] | { data: SessionSummary[] }>(`/sessions${scopedQuery(params)}`);
export const getSession = (id: string, params?: { type?: SessionType }) =>
  request<SessionSummary>(`/sessions/${seg(id)}${scopedQuery(params)}`);
export const getSessionRuns = (id: string, params?: { type?: SessionType }) =>
  request<SessionRunSummary[]>(`/sessions/${seg(id)}/runs${scopedQuery(params)}`);
export const getSessionRun = (id: string, runId: string, params?: { type?: SessionType }) =>
  request<SessionRunSummary>(`/sessions/${seg(id)}/runs/${seg(runId)}${scopedQuery(params)}`);
export const renameSession = (id: string, name: string, params?: { type?: SessionType }) =>
  request(`/sessions/${seg(id)}/rename${scopedQuery(params)}`, {
    method: 'POST',
    body: JSON.stringify({ session_name: name }),
  });
export const deleteSession = (id: string) =>
  request(`/sessions/${seg(id)}${scopedQuery()}`, { method: 'DELETE' });
export const deleteSessions = (ids: string[], types: SessionType[]) =>
  request(`/sessions${scopedQuery()}`, {
    method: 'DELETE',
    body: JSON.stringify({ session_ids: ids, session_types: types }),
  });

export const listMemories = (params?: { user_id?: string; topic?: string }) =>
  request<MemorySummary[] | { data: MemorySummary[] }>(
    `/memories${scopedQuery({ user_id: params?.user_id, topics: params?.topic })}`,
  );
export const getMemory = (id: string) => request<MemorySummary>(`/memories/${seg(id)}${scopedQuery()}`);
export const createMemory = (data: Partial<MemorySummary>) =>
  request<MemorySummary>(`/memories${scopedQuery()}`, { method: 'POST', body: JSON.stringify(data) });
export const updateMemory = (id: string, data: Partial<MemorySummary>) =>
  request(`/memories/${seg(id)}${scopedQuery()}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteMemory = (id: string) =>
  request(`/memories/${seg(id)}${scopedQuery()}`, { method: 'DELETE' });
export const listMemoryTopics = () => request<string[]>(`/memory_topics${scopedQuery()}`);
export const userMemoryStats = () =>
  request<Record<string, unknown>>(`/user_memory_stats${scopedQuery()}`);
export const optimizeMemories = () =>
  request(`/optimize-memories${scopedQuery()}`, { method: 'POST' });

export const knowledgeConfig = () => request<Record<string, unknown>>('/knowledge/config');
export const listKnowledgeContent = () =>
  request<KnowledgeContent[] | { data: KnowledgeContent[] }>('/knowledge/content');
export const getKnowledgeContent = (id: string) =>
  request<KnowledgeContent>(`/knowledge/content/${id}`);
export const getKnowledgeContentStatus = (id: string) =>
  request<Record<string, unknown>>(`/knowledge/content/${id}/status`);
export const createKnowledgeContent = (form: FormData) =>
  request<KnowledgeContent>('/knowledge/content', { method: 'POST', body: form });
export const createRemoteContent = (data: Record<string, unknown>) =>
  request<KnowledgeContent>('/knowledge/remote-content', {
    method: 'POST',
    body: JSON.stringify(data),
  });
export const deleteKnowledgeContent = (id: string) =>
  request(`/knowledge/content/${id}`, { method: 'DELETE' });
export const searchKnowledge = (query: string, limit = 10) =>
  request<unknown>('/knowledge/search', {
    method: 'POST',
    body: JSON.stringify({ query, limit }),
  });

export const listSchedules = () => request<Schedule[] | { data: Schedule[] }>('/schedules');
export const getSchedule = (id: string) => request<Schedule>(`/schedules/${id}`);
export const createSchedule = (data: Partial<Schedule>) =>
  request<Schedule>('/schedules', { method: 'POST', body: JSON.stringify(data) });
export const updateSchedule = (id: string, data: Partial<Schedule>) =>
  request<Schedule>(`/schedules/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteSchedule = (id: string) =>
  request(`/schedules/${id}`, { method: 'DELETE' });
export const enableSchedule = (id: string) =>
  request<Schedule>(`/schedules/${id}/enable`, { method: 'POST' });
export const disableSchedule = (id: string) =>
  request<Schedule>(`/schedules/${id}/disable`, { method: 'POST' });
export const triggerSchedule = (id: string) =>
  request<{ status: string; run_id?: string }>(`/schedules/${id}/trigger`, { method: 'POST' });

export const listApprovals = () => request<Approval[] | { data: Approval[] }>('/approvals');
export const createApproval = (data: Partial<Approval>) =>
  request<Approval>('/approvals', { method: 'POST', body: JSON.stringify(data) });
export const updateApproval = (id: string, data: Partial<Approval>) =>
  request(`/approvals`, { method: 'PATCH', body: JSON.stringify({ id, ...data }) });
export const deleteApproval = (id: string) =>
  request(`/approvals`, { method: 'DELETE', body: JSON.stringify({ id }) });

export const listEvalRuns = () => request<EvalRun[] | { data: EvalRun[] }>('/eval-runs');
export const getEvalRun = (id: string) => request<EvalRun>(`/eval-runs/${id}`);
export const createEvalRun = (data: Partial<EvalRun>) =>
  request<EvalRun>('/eval-runs', { method: 'POST', body: JSON.stringify(data) });
export const updateEvalRun = (id: string, data: Partial<EvalRun>) =>
  request(`/eval-runs/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteEvalRun = (id: string) =>
  request(`/eval-runs`, { method: 'DELETE', body: JSON.stringify({ id }) });

export const listTraces = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<TraceSummary[] | { data: TraceSummary[] }>(`/traces${qs}`);
};
export const getTrace = (id: string) => request<TraceSummary>(`/traces/${id}`);
export const tracesFilterSchema = () => request<Record<string, unknown>>('/traces/filter-schema');
export const searchTraces = (filters: Record<string, unknown>) =>
  request<TraceSummary[] | { data: TraceSummary[] }>('/traces/search', {
    method: 'POST',
    body: JSON.stringify(filters),
  });
export const traceSessionStats = (sessionId?: string) => {
  const qs = sessionId ? `?session_id=${sessionId}` : '';
  return request<Record<string, unknown>>(`/trace_session_stats${qs}`);
};

export const listModels = () => request<Record<string, unknown>>('/models');
export const metrics = () => request<Record<string, unknown>>('/metrics');
export const refreshMetrics = () => request('/metrics/refresh', { method: 'POST' });
